import { IntlShape } from "react-intl";
import { FieldValues, UseFormGetValues, UseFormSetError } from "react-hook-form";

import type { HandlingUnitHashMap, PackingListRow } from "@RequestFormV2/sections/PackingList/types";
import { isPackingListRowEmpty } from "@RequestFormV2/sections/PackingList/utils/isRowEmpty";
import { hasParcelHandlingUnit } from "@RequestFormV2/sections/PackingList/utils/parcelRequirements";
import { validatePackingListRow } from "@RequestFormV2/validation/sections/packingListFieldRules";

export type ValidatePackingListResult = {
  isValid: boolean;
  isEmpty: boolean;
};

type ValidatePackingListOptions = {
  isParcelFreight?: boolean;
  nmfcNumberEnabled?: string | null;
};

export function validatePackingList(
  getValues: UseFormGetValues<FieldValues>,
  setError: UseFormSetError<FieldValues>,
  intl: IntlShape,
  handlingUnitHashMap: HandlingUnitHashMap,
  options: ValidatePackingListOptions = {},
): ValidatePackingListResult {
  const rows = (getValues("packing_list") as PackingListRow[] | undefined) ?? [];
  const hasEntries = rows.some((row) => !isPackingListRowEmpty(row));
  const requiredMessage = intl.formatMessage({ id: "general.required", defaultMessage: "Required" });

  let isValid = true;

  // Parcel requires at least one handling unit. Customs fields are optional.
  // The synthetic path flags the PackingList section (and blocks Review); the inline
  // ParcelWarningBadge surfaces the message and turns red via hasAttemptedReview.
  if (options.isParcelFreight && !hasParcelHandlingUnit(rows)) {
    setError("packing_list.parcelHandlingUnit", { type: "manual", message: requiredMessage });
    isValid = false;
  }

  if (!hasEntries) {
    return { isValid, isEmpty: true };
  }

  rows.forEach((row, index) => {
    if (isPackingListRowEmpty(row)) return;

    const errorPaths = validatePackingListRow(row, index, handlingUnitHashMap, {
      nmfcNumberEnabled: options.nmfcNumberEnabled,
    });
    errorPaths.forEach((path) => {
      setError(path, { type: "manual", message: requiredMessage });
      isValid = false;
    });
  });

  return { isValid, isEmpty: false };
}
